import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ApiClient, ApiClientScalars, AuthTypeName } from "../../../../../../../shared/index";
import { AUTH_TYPE_LABELS } from "../../../../../../../shared/index";

const AUTH_TYPES: AuthTypeName[] = ["full", "limited", "denied", "singleFeature"];

/** Scalars and regional scopes. Edits are drafts until the page's single Save. */
export function OverviewTab({
  client,
  draft,
  onChange,
}: {
  client: ApiClient;
  draft: Partial<ApiClientScalars>;
  onChange: (patch: Partial<ApiClientScalars>) => void;
}) {
  const value = <K extends keyof ApiClientScalars>(key: K): ApiClientScalars[K] =>
    (draft[key] ?? (client as unknown as ApiClientScalars)[key]) as ApiClientScalars[K];

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section className="space-y-4 rounded-xl border bg-card p-6">
        <h2 className="font-semibold">Identity</h2>

        <div className="space-y-1.5">
          <Label>Client key</Label>
          <Input value={client.key} readOnly className="font-mono" />
          <p className="text-xs text-muted-foreground">
            Sent by the customer as the <code>X-APIKey</code> header. Changing it would break
            their integration, so it is fixed after creation.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="authType">Access type</Label>
          <select
            id="authType"
            className="h-9 w-full rounded-md border bg-background px-3 text-sm"
            value={value("authType")}
            onChange={(event) => onChange({ authType: event.target.value as AuthTypeName })}
          >
            {AUTH_TYPES.map((type) => (
              <option key={type} value={type}>
                {AUTH_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
          {value("authType") === "full" && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              A full client can call every endpoint; individual grants are ignored.
            </p>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="tenantId">Tenant id</Label>
            <Input
              id="tenantId"
              type="number"
              value={value("tenantId") ?? ""}
              onChange={(event) =>
                onChange({ tenantId: event.target.value === "" ? null : Number(event.target.value) })
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="environment">Environment</Label>
            <select
              id="environment"
              className="h-9 w-full rounded-md border bg-background px-3 text-sm"
              value={value("environment") ?? ""}
              onChange={(event) => onChange({ environment: event.target.value || null })}
            >
              <option value="">Not set</option>
              <option value="production">production</option>
              <option value="staging">staging</option>
            </select>
          </div>
        </div>
      </section>

      <section className="space-y-4 rounded-xl border bg-card p-6">
        <h2 className="font-semibold">Coverage</h2>

        <Toggle
          label="Active"
          description="Inactive clients are rejected by the API regardless of their grants."
          checked={value("isActive")}
          onChange={(checked) => onChange({ isActive: checked })}
        />
        <Toggle
          label="Allow Canadian coverage"
          checked={value("allowCanadian")}
          onChange={(checked) => onChange({ allowCanadian: checked })}
        />
        <Toggle
          label="Allow European coverage"
          checked={value("allowEuropean")}
          onChange={(checked) => onChange({ allowEuropean: checked })}
        />
        <Toggle
          label="Include unranked experts"
          checked={value("includeUnranked")}
          onChange={(checked) => onChange({ includeUnranked: checked })}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="period">Period</Label>
            <Input
              id="period"
              type="number"
              value={value("period")}
              onChange={(event) => onChange({ period: Number(event.target.value) })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="benchmark">Benchmark</Label>
            <Input
              id="benchmark"
              type="number"
              value={value("benchmark")}
              onChange={(event) => onChange({ benchmark: Number(event.target.value) })}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Country scopes</Label>
          <p className="text-sm text-muted-foreground">
            {client.countries.filter((c) => c.isActive).length} active
            {client.countries.some((c) => !c.isActive) &&
              ` · ${client.countries.filter((c) => !c.isActive).length} disabled`}
          </p>
        </div>

        <div className="space-y-1.5">
          <Label>News topics</Label>
          <p className="text-sm text-muted-foreground">
            {client.newsCategories.filter((n) => n.isActive).map((n) => n.slug).join(", ") ||
              "None — the client receives every topic"}
          </p>
        </div>
      </section>
    </div>
  );
}

function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1"
      />
      <span>
        <span className="text-sm font-medium">{label}</span>
        {description && <span className="block text-xs text-muted-foreground">{description}</span>}
      </span>
    </label>
  );
}
